import React, { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useFetch } from "../../../hooks/useFetch";
import * as formAnswerService from "../../../services/formAnswerService";
import { FormAnswerListItemDto } from "../../../models/formAnswer/formAnswerListItemDto";
import { FormAnswerPreviewDialog } from "../../reporting/formSummaryView/formAnswerPreviewDialog";
import { Base } from "../../../framework/base";

export const VehicleFormAnswersPanel = ({
    vehicleId,
}: {
    vehicleId: string;
}) => {
    const [rows, setRows] = useState<FormAnswerListItemDto[] | null>(null);
    const [selectedAnswer, setSelectedAnswer] = useState<{
        id: string;
        formName: string;
    } | null>(null);

    useFetch(
        (signal) =>
            formAnswerService.getFormAnswers(
                {
                    relatedObjectType: "Vehicle",
                    relatedObjectIds: [vehicleId],
                },
                signal
            ),
        {
            onSuccess: (response) => setRows(response.data),
            withLoadingIndicator: false,
        },
        [vehicleId]
    );

    if (rows === null) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (rows.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                p: 2,
                backgroundColor: "grey.50",
            }}
            data-testid="vehicle-form-answers-panel"
        >
            {rows.map((row) => (
                <Card
                    key={row.formAnswerId}
                    variant="outlined"
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    data-testid={`vehicle-form-answer-${row.formAnswerId}`}
                >
                    <CardActionArea
                        onClick={() =>
                            setSelectedAnswer({
                                id: row.formAnswerId,
                                formName: row.formName,
                            })
                        }
                    >
                        <CardContent>
                            <Typography variant="subtitle2" noWrap>
                                {row.formName}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {Base.dayjsDateToDateTimeStr(row.answeredAt)}
                            </Typography>
                            {row.answeredByEmployeeName && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                >
                                    {row.answeredByEmployeeName}
                                </Typography>
                            )}
                        </CardContent>
                    </CardActionArea>
                </Card>
            ))}
            {selectedAnswer && (
                <FormAnswerPreviewDialog
                    formAnswerId={selectedAnswer.id}
                    formName={selectedAnswer.formName}
                    onClose={() => setSelectedAnswer(null)}
                />
            )}
        </Box>
    );
};
